import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, FileSpreadsheet, FileText, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  FLOW_COMPLETION_BAR_FILL_GRADIENT,
  FLOW_COMPLETION_BAR_FILL_OPACITY,
  FLOW_COMPLETION_BAR_HEIGHT_PX,
  FLOW_COMPLETION_BAR_TRACK_COLOR,
  OVERVIEW_CARD_CLASSNAME,
} from "./constants";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";

export type FunnelStep = {
  title: string;
  step: string;
  completed: number;
  total: number;
};

interface FlowCompletionCardProps {
  title: string;
  steps: FunnelStep[];
  exportLabel?: string;
  className?: string;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function toPercent(completed: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  const percent = Math.round((Math.max(0, completed) / total) * 100);

  return Math.min(100, percent);
}

export const FlowCompletionCard: React.FC<FlowCompletionCardProps> = ({
  title,
  steps,
  exportLabel: exportLabelProp,
  className,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();
  const exportLabel = exportLabelProp ?? t("analytics.common.export", "Export");

  return (
    <div className={cn(OVERVIEW_CARD_CLASSNAME, "min-w-0", className)}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold text-[#171717]">{title}</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={exportDisabled}>
            <Button
              type="button"
              variant="span"
              className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
            >
              <Share className="size-3.5" />
              {exportLabel}
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport?.("xlsx")}>
              <FileSpreadsheet className="size-4" />
              {t("analytics.common.xlsx", "XLSX")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.("csv")}>
              <FileText className="size-4" />
              {t("analytics.common.csv", "CSV")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-4 pr-1">
        {steps.map((step) => {
          const percent = toPercent(step.completed, step.total);
          const dropoffCount = Math.max(0, step.total - step.completed);
          const dropoffPercent = 100 - percent;

          return (
            <div key={step.title} className="min-w-0 space-y-1.5">
              <p className="text-sm leading-normal font-medium text-[#171717]">
                {step.title}
              </p>
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="relative min-w-0 flex-1 overflow-hidden rounded-sm"
                  style={{
                    height: FLOW_COMPLETION_BAR_HEIGHT_PX,
                    backgroundColor: FLOW_COMPLETION_BAR_TRACK_COLOR,
                  }}
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-sm"
                    style={{
                      width: `${percent}%`,
                      background: FLOW_COMPLETION_BAR_FILL_GRADIENT,
                      opacity: FLOW_COMPLETION_BAR_FILL_OPACITY,
                    }}
                  />
                  <span className="relative z-10 flex h-full items-center px-3 text-sm font-medium text-[#171717]">
                    {step.step}
                  </span>
                </div>
                <div className="flex shrink-0 flex-col justify-center gap-0.5 text-right">
                  <div className="text-xs leading-normal font-semibold whitespace-nowrap text-[#171717]">
                    {percent}%{" "}
                    <span className="font-normal text-[#EF4444]">
                      (-{dropoffPercent}%)
                    </span>
                  </div>
                  <div className="text-xs leading-normal font-normal whitespace-nowrap text-[#737373]">
                    {step.total} <span>(-{dropoffCount})</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
